import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import {
  CLIENT_TYPES,
  LEAD_SOURCES,
  PURCHASE_HORIZONS,
  TEMPERATURES,
  type Lead,
} from '@/types/app';
import { useCreateLead } from './useLeads';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (lead: Lead, wasDuplicate: boolean) => void;
}

export function CreateLeadDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const createLead = useCreateLead();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('walk_in');
  const [clientType, setClientType] = useState('');
  const [temperature, setTemperature] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [areas, setAreas] = useState('');
  const [horizon, setHorizon] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFullName('');
    setPhone('');
    setWhatsapp('');
    setEmail('');
    setSource('walk_in');
    setClientType('');
    setTemperature('');
    setBudgetMin('');
    setBudgetMax('');
    setAreas('');
    setHorizon('');
    setError(null);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      const lead = await createLead.mutateAsync({
        full_name: fullName,
        phone,
        source: source as never,
        whatsapp_phone: whatsapp.trim() || null,
        email: email.trim() || null,
        client_type: clientType || null,
        temperature: temperature || null,
        budget_min: budgetMin ? Number(budgetMin) : null,
        budget_max: budgetMax ? Number(budgetMax) : null,
        preferred_areas: areas
          .split(/[،,]/)
          .map((a) => a.trim())
          .filter(Boolean),
        purchase_horizon: horizon || null,
      });

      // A duplicate is a normal outcome, not an error (#3): create_lead() returns
      // the existing record. duplicate_hits > 0 is how we know, and the caller
      // navigates to that lead rather than pretending a new one was made.
      onCreated(lead, lead.duplicate_hits > 0);
      reset();
      onClose();
    } catch (cause) {
      // The database raises a readable message for a bad phone number, which is
      // more useful than anything generic we could substitute.
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('leads.create')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('leads.fullName')}
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input
            label={t('leads.phone')}
            required
            dir="ltr"
            placeholder="01001234567"
            hint={t('leads.phoneHint')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label={t('leads.whatsapp')}
            dir="ltr"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
          />
          <Input
            label={t('auth.email')}
            type="email"
            dir="ltr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Select
            label={t('leads.source')}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={LEAD_SOURCES.map((s) => ({
              value: s,
              label: t(`sources.${s}` as 'sources.meta'),
            }))}
          />
          <Select
            label={t('leads.clientType')}
            placeholder="—"
            value={clientType}
            onChange={(e) => setClientType(e.target.value)}
            options={CLIENT_TYPES.map((c) => ({
              value: c,
              label: t(`clientTypes.${c}` as 'clientTypes.end_user'),
            }))}
          />
          <Select
            label={t('leads.temperature')}
            placeholder="—"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            options={TEMPERATURES.map((x) => ({
              value: x,
              label: t(`temperatures.${x}` as 'temperatures.hot'),
            }))}
          />
          <Select
            label={t('leads.horizon')}
            placeholder="—"
            value={horizon}
            onChange={(e) => setHorizon(e.target.value)}
            options={PURCHASE_HORIZONS.map((h) => ({
              value: h,
              label: t(`horizons.${h}` as 'horizons.immediate'),
            }))}
          />
          <Input
            label={t('leads.budgetMin')}
            type="number"
            dir="ltr"
            value={budgetMin}
            onChange={(e) => setBudgetMin(e.target.value)}
          />
          <Input
            label={t('leads.budgetMax')}
            type="number"
            dir="ltr"
            value={budgetMax}
            onChange={(e) => setBudgetMax(e.target.value)}
          />
        </div>

        <Input
          label={t('leads.areas')}
          hint={t('admin.teams.areasHint')}
          value={areas}
          onChange={(e) => setAreas(e.target.value)}
        />

        <p className="text-xs text-slate-500">{t('leads.assignmentNote')}</p>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={createLead.isPending}>
            {t('leads.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
